import React from 'react';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Check, ArrowRight, Zap, Star, ChevronRight, X, CheckCircle2, ShieldCheck, Cloud, Headphones, Quote } from 'lucide-react';
import { SEGMENT_LIST } from '@/lib/segments';
import { CITY_LIST } from '@/lib/cities';
import { mediaUrl } from '@/components/ImageUploadButton';

export const OG_IMAGE = 'https://nfcesys.com.br/og-image.png';
const CTA = 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0';

export const LandingHeader = ({ navigate }) => (
  <header className="fixed top-0 inset-x-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
      <a href="/" className="flex items-center gap-3" data-testid="segment-logo-link">
        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
          <ShoppingCart className="w-5 h-5 text-white" />
        </div>
        <span className="text-lg font-bold text-white">nfcesysPdv</span>
      </a>
      <div className="flex items-center gap-2">
        <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate('/login')} data-testid="segment-login-btn">Entrar</Button>
        <Button className={CTA} onClick={() => navigate('/?cadastro=1')} data-testid="segment-header-cta">Teste Grátis</Button>
      </div>
    </div>
  </header>
);

export const LandingHero = ({ badge, Icon, h1a, h1b, h1c, sub, navigate }) => (
  <section className="relative pt-28 sm:pt-36 pb-16 px-4 sm:px-6 overflow-hidden">
    <div className="absolute top-20 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
    <div className="relative max-w-5xl mx-auto">
      <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 text-amber-400 px-4 py-2 rounded-full text-sm mb-8">
        <Icon className="w-4 h-4" /><span>{badge}</span><ChevronRight className="w-4 h-4" />
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1] tracking-tight" data-testid="segment-h1">
        {h1a}{' '}<span className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent">{h1b}</span>
        <br />{h1c}
      </h1>
      <h2 className="text-base md:text-lg text-slate-400 max-w-2xl mb-10 leading-relaxed">{sub}</h2>
      <div className="flex flex-col sm:flex-row gap-4 mb-10">
        <Button size="lg" className={`${CTA} text-lg px-8 py-7 rounded-xl shadow-2xl shadow-amber-500/30 group`} onClick={() => navigate('/?cadastro=1')} data-testid="segment-hero-cta">
          <Zap className="w-5 h-5 mr-2" />Começar Grátis - 30 Dias<ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
        <Button size="lg" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800/50 text-lg px-8 py-7 rounded-xl" onClick={() => navigate('/#planos')} data-testid="segment-plans-btn">Ver Planos e Preços</Button>
      </div>
      <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
        <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" />Sem cartão de crédito</span>
        <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" />Sem fidelidade</span>
        <span className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" />NFC-e homologada SEFAZ</span>
        <span className="flex items-center gap-1"><Star className="w-4 h-4 text-amber-400 fill-amber-400" />4.9 (1.250 lojistas)</span>
      </div>
    </div>
  </section>
);

export const LandingDores = ({ titulo, dores }) => (
  <section className="py-16 px-4 sm:px-6 bg-slate-900/50 border-y border-slate-800">
    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-start">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{titulo}</h2>
        <p className="text-slate-400">Criamos o nfcesysPdv para resolver exatamente esses problemas — sem módulos inúteis e sem mensalidade abusiva.</p>
      </div>
      <ul className="space-y-3">
        {dores.map((d) => (
          <li key={d} className="flex items-start gap-3 bg-slate-950 border border-slate-800 rounded-xl p-4 text-slate-300"><X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />{d}</li>
        ))}
      </ul>
    </div>
  </section>
);

export const LandingFeatures = ({ titulo, features }) => (
  <section className="py-20 px-4 sm:px-6">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{titulo}</h2>
      <p className="text-slate-400 mb-12 max-w-2xl">Sistema simples de aprender, rápido de implantar e com o melhor custo-benefício. Você começa a vender hoje.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f) => (
          <div key={f.t} className="group bg-slate-900/60 border border-slate-800 hover:border-amber-500/40 rounded-2xl p-6 transition-colors" data-testid="segment-feature-card">
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><f.icon className="w-5 h-5" /></div>
            <h3 className="text-white font-semibold mb-2">{f.t}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{f.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const LandingDepoimentos = ({ depoimentos, logos, real = false }) => (
  <section className="py-20 px-4 sm:px-6 bg-slate-900/40 border-y border-slate-800" data-testid="segment-social-proof">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Quem usa, recomenda</h2>
          <p className="text-slate-400">Lojistas que trocaram planilha e sistema caro pelo nfcesysPdv.</p>
        </div>
        <div className="flex items-center gap-1 text-amber-400">
          {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400" />)}
          <span className="text-slate-400 text-sm ml-2">4.9 de 5 • 1.250 avaliações</span>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-5 mb-12">
        {depoimentos.map((d) => (
          <figure key={d.nome} className="relative bg-slate-950 border border-slate-800 rounded-2xl p-6 flex flex-col" data-testid="segment-testimonial">
            <Quote className="w-8 h-8 text-amber-500/30 absolute top-5 right-5" />
            <blockquote className="text-slate-300 leading-relaxed flex-1">“{d.texto}”</blockquote>
            <figcaption className="mt-6 flex items-center gap-3">
              {d.logo ? (
                <img src={mediaUrl(d.logo)} alt={d.negocio} className="w-11 h-11 rounded-full object-cover border border-slate-700 bg-white" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 text-white font-bold flex items-center justify-center">{d.nome.split(' ').map((n) => n[0]).slice(0, 2).join('')}</div>
              )}
              <div>
                <div className="text-white font-semibold text-sm">{d.nome}</div>
                <div className="text-slate-500 text-xs">{d.negocio} • {d.cidade}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6" data-testid="segment-client-logos">
        {logos.map((l) => (
          <div key={l.nome || l} className="px-5 py-2.5 rounded-lg border border-slate-800 bg-slate-950/60 text-slate-400 font-semibold tracking-wide text-sm uppercase flex items-center gap-2">
            {l.logo && <img src={mediaUrl(l.logo)} alt="" className="w-6 h-6 rounded object-cover bg-white" />}{l.nome || l}
          </div>
        ))}
      </div>
      {!real && <p className="text-center text-[11px] text-slate-600 mt-6" data-testid="segment-illustrative-note">Depoimentos e marcas ilustrativos — substituídos por clientes reais mediante autorização.</p>}
    </div>
  </section>
);

export const LandingGarantias = () => (
  <section className="py-14 px-4 sm:px-6 bg-gradient-to-r from-amber-500/10 to-orange-600/10 border-y border-amber-500/20">
    <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-8">
      {[
        { i: Cloud, t: '100% na nuvem', d: 'Acesse de qualquer computador, tablet ou celular. Sem instalação, sem servidor.' },
        { i: ShieldCheck, t: 'Homologado SEFAZ', d: 'NFC-e e NF-e emitidas com contingência automática em todos os estados.' },
        { i: Headphones, t: 'Suporte humano', d: 'Atendimento por WhatsApp com gente de verdade para implantar e treinar sua equipe.' },
      ].map((g) => (
        <div key={g.t} className="flex gap-4"><g.i className="w-8 h-8 text-amber-400 shrink-0" /><div><h3 className="text-white font-semibold">{g.t}</h3><p className="text-sm text-slate-400">{g.d}</p></div></div>
      ))}
    </div>
  </section>
);

export const LandingFaq = ({ faq }) => (
  <section className="py-20 px-4 sm:px-6">
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10">Perguntas frequentes</h2>
      <div className="space-y-4">
        {faq.map((f) => (
          <details key={f.q} className="group bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="segment-faq-item">
            <summary className="cursor-pointer text-white font-medium flex items-center justify-between list-none">{f.q}<ChevronRight className="w-4 h-4 text-slate-500 group-open:rotate-90 transition-transform" /></summary>
            <p className="text-slate-400 mt-3 text-sm leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

export const LandingCtaFinal = ({ titulo, navigate }) => (
  <section className="py-20 px-4 sm:px-6">
    <div className="max-w-4xl mx-auto bg-slate-900 border border-slate-800 rounded-3xl p-10 sm:p-14 relative overflow-hidden">
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl" />
      <h2 className="relative text-3xl sm:text-4xl font-bold text-white mb-4">{titulo}</h2>
      <p className="relative text-slate-400 mb-8">30 dias grátis, sem cartão e sem fidelidade. Se não gostar, é só não continuar.</p>
      <Button size="lg" className={`relative ${CTA} text-lg px-8 py-7 rounded-xl`} onClick={() => navigate('/?cadastro=1')} data-testid="segment-final-cta"><CheckCircle2 className="w-5 h-5 mr-2" />Criar minha conta grátis</Button>
    </div>
  </section>
);

export const LandingLinks = ({ currentSegment, currentCity }) => (
  <section className="py-12 px-4 sm:px-6 border-t border-slate-800">
    <div className="max-w-5xl mx-auto space-y-8">
      <div>
        <h2 className="text-base md:text-lg font-semibold text-white mb-4">Soluções por segmento</h2>
        <div className="flex flex-wrap gap-3">
          {SEGMENT_LIST.filter((s) => s.key !== currentSegment).map((s) => (
            <a key={s.key} href={`/${s.slug}`} className="inline-flex items-center gap-2 bg-slate-900 border border-slate-800 hover:border-amber-500/40 text-slate-300 hover:text-white px-4 py-2 rounded-full text-sm transition-colors" data-testid={`segment-link-${s.key}`}><s.icon className="w-4 h-4 text-amber-400" />PDV para {s.nome}</a>
          ))}
        </div>
      </div>
      <div>
        <h2 className="text-base md:text-lg font-semibold text-white mb-4">Sistema PDV na sua cidade</h2>
        <div className="flex flex-wrap gap-3">
          {CITY_LIST.filter((c) => c.key !== currentCity).map((c) => (
            <a key={c.key} href={`/${c.slug}`} className="inline-flex items-center gap-2 bg-slate-900 border border-slate-800 hover:border-amber-500/40 text-slate-300 hover:text-white px-4 py-2 rounded-full text-sm transition-colors" data-testid={`city-link-${c.key}`}>Sistema PDV em {c.nome}</a>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-600">© {new Date().getFullYear()} nfcesysPdv • <a href="/" className="hover:text-slate-400">Página inicial</a> • <a href="/blog" className="hover:text-slate-400">Blog</a></p>
    </div>
  </section>
);
